import { FC, useState } from 'react';
import { useAuthResetPasswordRequest } from './queries/authForgotPassword.generated';
import { useParams } from 'react-router-dom';
import { Button, Form } from 'antd';
import { PasswordInput } from 'antd-password-input-strength';
import { useGettext } from '@cranium/i18n';
import type { IdentityTypeEnum } from '@/gql/types.generated';
import { Title } from '@pages/Login/component';
import styled from 'styled-components';
import { Step } from './types';

type NewPasswordProps = {
  setStep: (data: Step) => void;
  email: string;
  otp: string;
};

export const NewPassword: FC<NewPasswordProps> = ({ setStep, email, otp }) => {
  const { gettext } = useGettext();
  const [level, setLevel] = useState(0);
  const { identity } = useParams<{ identity: IdentityTypeEnum }>();
  const recoverPassword = useAuthResetPasswordRequest();
  const [form] = Form.useForm<{ password: string }>();

  const handleSubmit = () => {
    form.validateFields().then(values => {
      return recoverPassword({
        email: email,
        type: identity,
        newPassword: values.password,
        resetToken: otp
      }).then(resp => {
        if (resp?.data?.authResetPassword?.success) {
          setStep({ step: 3 });
        }
      });
    });
  };

  return (
    <Main>
      <Title>{gettext('Enter New Password')}</Title>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label={gettext('Password')}
          name="password"
          rules={[
            { required: true, message: gettext('Required') },
            {
              validator: async () => {
                return level >= 1 ? Promise.resolve() : Promise.reject(gettext('Password is too weak'));
              },
              message: gettext('Password is too weak')
            }
          ]}
        >
          <PasswordInput onLevelChange={setLevel} />
        </Form.Item>
        <Info>
          <p>{gettext('Your password should contain:')}</p>
          <ul>
            <li>{gettext('At least one lowercase letter')}</li>
            <li>{gettext('At least one number')}</li>
            <li>{gettext('A Minimum of 8 characters')}</li>
          </ul>
        </Info>
        <Button type="primary" onClick={() => handleSubmit()}>
          {gettext('Next')}
        </Button>
      </Form>
    </Main>
  );
};

const Info = styled.div`
  font: var(--font-regular-14);
  color: var(--gray-7);
  p {
    margin-bottom: 4px;
  }
  ul {
    padding-left: 20px;
  }
`;

const Main = styled.div`
  width: 420px;
  max-width: 100%;
  > h1 {
    margin-bottom: 0;
  }
  > form {
    display: flex;
    flex-direction: column;
  }
  button {
    align-self: flex-end;
  }
  a {
    display: flex;
    align-items: center;
    gap: 4px;
    &:hover {
      color: var(--green-7);
      text-decoration: none;
    }
  }
`;
